<template>
  <div class="did-login">
    <!-- Connected State -->
    <div v-if="isConnected" class="connected">
      <span class="connected-did">
        <DidIcon class="icon" />
        {{ displayName }}
      </span>
      <button v-if="allowDisconnect" @click="disconnect" class="btn-text">disconnect</button>
    </div>

    <!-- Not Connected State -->
    <div v-else class="login-row">
      <button @click="connectDidWallet" :disabled="isLoading" class="btn btn-did">
        <DidIcon class="icon" />
        <span>{{ isLoading ? '...' : 'DecastID' }}</span>
      </button>

      <button v-if="allowGuest" @click="$emit('guest')" class="btn btn-guest">
        Guest login
      </button>
    </div>

    <p v-if="statusMessage" class="status" :class="statusType">{{ statusMessage }}</p>

    <!-- Extension Not Found Modal -->
    <div v-if="showExtensionModal" class="modal-overlay" @click.self="showExtensionModal = false">
      <div class="modal">
        <button class="modal-close" @click="showExtensionModal = false">&times;</button>

        <div class="modal-header">
          <DidIcon class="modal-icon" />
          <h2>Get DecastID</h2>
        </div>

        <p class="modal-intro">
          You need the DecastID extension to continue. It takes less than a minute to set up.
        </p>

        <a :href="extensionUrl" target="_blank" class="install-btn">
          Install Extension
        </a>

        <div class="faq">
          <details>
            <summary>What is DecastID?</summary>
            <p>It's your personal digital ID for Decast. Think of it like a username that you fully own and control.</p>
          </details>

          <details>
            <summary>Why do I need it?</summary>
            <p>It lets you join sessions with a verified identity. Hosts can see you're a real person, not a random guest.</p>
          </details>

          <details>
            <summary>Is it free?</summary>
            <p>Yes, completely free. Just install the Chrome extension and create your ID in seconds.</p>
          </details>

          <details>
            <summary>What if I don't use Chrome?</summary>
            <p>The extension currently works on Chrome and Chromium-based browsers like Brave and Edge.</p>
          </details>
        </div>

        <p class="modal-footer">
          Already installed? <button class="link-btn" @click="retryConnection">Try again</button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import DidIcon from '@/decast/icons/DidIcon.vue';
import constants from '@/../constant.js';

const EXTENSION_ID = constants.didManagerExtensionKey;

export default {
  name: 'DidLogin',
  components: { DidIcon },
  props: {
    allowGuest: { type: Boolean, default: false },
    allowDisconnect: { type: Boolean, default: true },
    initialDid: { type: String, default: '' },
    requireSignature: { type: Boolean, default: false }
  },
  data() {
    return {
      did: this.initialDid || '',
      address: '',
      username: '',
      isLoading: false,
      walletType: '',
      statusMessage: '',
      statusType: '',
      nonceToken: '',
      showExtensionModal: false,
      extensionUrl: `https://chrome.google.com/webstore/detail/decast-did-manager/${EXTENSION_ID}`,
    };
  },
  computed: {
    isConnected() { return !!this.did; },
    displayName() {
      if (!this.did) return '';
      // Prefer username if available (e.g., "ahmad.decast")
      if (this.username) return this.username;
      // Fallback to shortened DID/address
      if (this.did.startsWith('did:decast:')) {
        const id = this.did.replace('did:decast:', '');
        return `${id.slice(0, 4)}...${id.slice(-3)}`;
      }
      if (this.did.startsWith('did:ethr:')) {
        const addr = this.did.replace('did:ethr:', '');
        return `${addr.slice(0, 4)}...${addr.slice(-3)}`;
      }
      return this.did.length > 12 ? `${this.did.slice(0, 6)}...${this.did.slice(-3)}` : this.did;
    }
  },
  mounted() {
    window.addEventListener('message', this.handleDidMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleDidMessage);
  },
  methods: {
    async connectDidWallet() {
      this.isLoading = true;
      this.statusMessage = '';

      // Check if we're in a Chrome-based browser
      if (!window.chrome || !chrome.runtime) {
        this.isLoading = false;
        this.showExtensionModal = true;
        return;
      }

      chrome.runtime.sendMessage(EXTENSION_ID, { action: 'open-did-popup' }, (response) => {
        if (chrome.runtime.lastError) {
          this.isLoading = false;
          this.showExtensionModal = true;
        }
      });
    },

    retryConnection() {
      this.showExtensionModal = false;
      this.connectDidWallet();
    },

    handleDidMessage(event) {
      if (event.data?.action === 'did-selected') this.handleDidSelected(event.data);
      else if (event.data?.action === 'nonce-signed') this.handleNonceSigned(event.data);
    },

    async handleDidSelected(data) {
      try {
        const { did, username } = data;
        console.log('[DidLogin] Received did-selected:', { did, username, fullData: data });

        // Handle both string and object formats from extension (for backwards compatibility)
        if (typeof did === 'string') {
          this.did = did;
          this.username = username || '';
        } else {
          // Legacy format: { did: { did: "string" } }
          this.did = did?.did || '';
          this.username = did?.username || username || '';
        }
        console.log('[DidLogin] Parsed DID:', this.did, 'Username:', this.username);

        this.walletType = 'DecastID';

        if (this.requireSignature) await this.requestNonceSignature();
        else this.completeConnection();
      } catch (err) {
        this.isLoading = false;
        this.statusMessage = err.message;
        this.statusType = 'error';
      }
    },

    async requestNonceSignature() {
      try {
        this.statusMessage = 'Signing...';
        this.statusType = 'info';
        const payload = { did: this.did };
        const response = await this.$store.dispatch('auth/v2getDidNonce', payload);
        const { nonce, token } = response.data;
        this.nonceToken = token;
        chrome.runtime.sendMessage(EXTENSION_ID, { action: 'sign-nonce', nonce, did: this.did }, (response) => {
          if (chrome.runtime.lastError) this.completeConnection();
        });
      } catch (error) {
        this.completeConnection();
      }
    },

    handleNonceSigned(data) {
      if (data.error) {
        this.statusMessage = 'Skipped';
        this.statusType = 'warning';
      }
      this.completeConnection(data.signature);
    },

    completeConnection(signature = null) {
      this.isLoading = false;
      this.statusMessage = '';
      this.$emit('connected', {
        did: this.did,
        address: this.address,
        username: this.username,
        walletType: this.walletType,
        displayName: this.displayName,
        fullDid: this.did,
        signature,
        nonceToken: this.nonceToken,
      });
    },

    disconnect() {
      const previousDid = this.did;
      this.did = '';
      this.address = '';
      this.username = '';
      this.walletType = '';
      this.nonceToken = '';
      this.$emit('disconnected', { previousDid });
    }
  }
};
</script>

<style scoped>
.did-login {
  font-family: 'JetBrains Mono', monospace;
}

/* Connected */
.connected {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.connected-did {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  border-radius: 6px;
  font-size: 14px;
  color: #22C55E;
}

.connected-did .icon {
  width: 16px;
  height: 16px;
}

.btn-text {
  background: none;
  border: none;
  color: #888;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
}

.btn-text:hover {
  color: #ef4444;
}

/* Login Row */
.login-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: 1px solid;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn .icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* DecastID */
.btn-did {
  background: #000;
  border: 2px solid #D7DF23;
  color: #D7DF23;
}

.btn-did:hover:not(:disabled) {
  background: #111;
  border-color: #e5ed31;
  color: #e5ed31;
}

/* Guest/Skip */
.btn-guest {
  background: #1a1a1a;
  border: 1px solid #444;
  color: rgba(255, 255, 255, 0.7);
  padding: 12px 18px;
  font-size: 14px;
}

.btn-guest:hover {
  background: #252525;
  border-color: #555;
  color: rgba(255, 255, 255, 0.9);
}

/* Status */
.status {
  display: inline-block;
  margin-left: 12px;
  font-size: 12px;
  padding: 6px 10px;
}

.status.info { color: #3b82f6; }
.status.warning { color: #f59e0b; }
.status.error { color: #ef4444; }

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 400px;
  width: 100%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
}

.modal * {
  background: transparent !important;
  border: none !important;
}

.modal .install-btn {
  background: #D7DF23 !important;
}

.modal .faq {
  border-top: 1px solid #333 !important;
}

.modal .faq details p {
  border-bottom: 1px solid #2a2a2a !important;
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #666;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.modal-close:hover {
  color: #fff;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  background: transparent;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #fff;
  font-weight: 600;
}

.modal-icon {
  width: 32px;
  height: 32px;
  background: transparent;
}

.modal-intro {
  color: #aaa;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
}

.install-btn {
  display: block;
  width: 100%;
  padding: 14px;
  background: #D7DF23;
  color: #000;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  margin-bottom: 24px;
  transition: background 0.15s;
}

.install-btn:hover {
  background: #e5ed31;
}

/* FAQ */
.faq {
  border-top: 1px solid #333;
  padding-top: 20px;
}

.faq details {
  margin-bottom: 12px;
}

.faq summary {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  list-style: none;
}

.faq summary::-webkit-details-marker {
  display: none;
}

.faq summary::before {
  content: '+';
  display: inline-block;
  width: 20px;
  color: #D7DF23;
  font-weight: bold;
}

.faq details[open] summary::before {
  content: '-';
}

.faq details p {
  color: #888;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 8px 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #2a2a2a;
}

.modal-footer {
  margin-top: 20px;
  text-align: center;
  color: #666;
  font-size: 12px;
}

.link-btn {
  background: none;
  border: none;
  color: #D7DF23;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
  padding: 0;
}

.link-btn:hover {
  color: #e5ed31;
}

/* Mobile */
@media (max-width: 480px) {
  .login-row {
    gap: 8px;
  }

  .btn {
    padding: 10px 14px;
    font-size: 13px;
  }

  .btn .icon {
    width: 16px;
    height: 16px;
  }

  .btn-guest {
    padding: 10px 14px;
    font-size: 13px;
  }

  .status {
    display: block;
    margin-left: 0;
    margin-top: 8px;
  }

  .modal {
    padding: 24px;
  }

  .modal-header h2 {
    font-size: 18px;
  }
}
</style>
